/**
 * SEO Component - Handles all meta tags, Open Graph, Twitter Cards, and Structured Data
 * Uses native document manipulation for React 19 compatibility
 * (react-helmet-async v2.0 has known issues with React 19)
 */
import { useEffect } from 'react';

const SITE_NAME = 'The Cool-Girl Cut';
const DEFAULT_DESCRIPTION = 'Curated Amazon finds for the cool girls. Shop fashion, beauty & lifestyle with that effortless cool-girl energy.';
const DEFAULT_IMAGE = '/og-image.png';

/**
 * Helper to get or create a meta tag
 */
const setMetaTag = (selector, content, createAttr = {}) => {
  if (typeof document === 'undefined') return;
  let tag = document.querySelector(selector);
  if (!tag) {
    tag = document.createElement('meta');
    Object.entries(createAttr).forEach(([key, value]) => {
      tag.setAttribute(key, value);
    });
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
};

/**
 * Helper to set or update link tag
 */
const setLinkTag = (rel, href) => {
  if (typeof document === 'undefined') return;
  let link = document.querySelector(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', rel);
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
};

/**
 * Helper to set or update JSON-LD script
 */
const setJsonLd = (id, data) => {
  if (typeof document === 'undefined' || !data) return;
  let script = document.getElementById(id);
  if (!script) {
    script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
};

/**
 * Helper to remove JSON-LD script
 */
const removeJsonLd = (id) => {
  if (typeof document === 'undefined') return;
  const script = document.getElementById(id);
  if (script) script.remove();
};

/**
 * SEO Component for all pages
 * @param {Object} props
 * @param {string} props.title - Page title
 * @param {string} props.description - Page description
 * @param {string} props.image - OG image URL
 * @param {string} props.url - Canonical URL
 * @param {string} props.type - og:type (website, article, product)
 * @param {Object} props.product - Product data for structured data
 * @param {Object} props.breadcrumbs - Breadcrumb data for structured data
 * @param {Array} props.productList - Array of products for ItemList schema (for Editor's Picks, etc.)
 * @param {boolean} props.noIndex - Whether to noindex the page
 */
const SEO = ({ 
  title, 
  description, 
  keywords,
  image, 
  url, 
  type = 'website',
  product = null,
  breadcrumbs = null,
  itemList = null,  // For category/subcategory pages - array of {name, url, image}
  productList = null, // For product collections like Editor's Picks - array of {name, description, image, url, category}
  webPage = null,   // For static/article pages - {name, description, dateModified, datePublished}
  isHomepage = false,  // For WebSite schema with SearchAction
  region = null,  // 'US' or 'UK' for hreflang
  noIndex = false
}) => {
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const siteUrl = window.location.origin;
    
    // Build values
    const safeTitle = (title && typeof title === 'string' && title.trim().length > 0) 
      ? title.trim() 
      : '';
    // If a title is provided, format as "Title | Site Name"
    // If no title, just use the site name alone
    const pageTitle = safeTitle ? `${safeTitle} | ${SITE_NAME}` : SITE_NAME;
    
    const pageDescription = (description && typeof description === 'string' && description.trim().length > 0)
      ? description.trim()
      : DEFAULT_DESCRIPTION;
    
    let pageImage = `${siteUrl}${DEFAULT_IMAGE}`;
    if (image && typeof image === 'string' && image.length > 0) {
      pageImage = image.startsWith('http') ? image : `${siteUrl}${image}`;
    }
    
    const pageUrl = (url && typeof url === 'string') 
      ? (url.startsWith('http') ? url : `${siteUrl}${url}`)
      : window.location.href;

    // Set document title
    document.title = pageTitle;

    // Primary Meta Tags
    setMetaTag('meta[name="title"]', pageTitle, { name: 'title' });
    setMetaTag('meta[name="description"]', pageDescription, { name: 'description' });
    
    // Keywords (if provided)
    if (keywords && typeof keywords === 'string' && keywords.trim().length > 0) {
      setMetaTag('meta[name="keywords"]', keywords.trim(), { name: 'keywords' });
    }
    
    // Robots - always explicitly set to ensure correct value
    if (noIndex) {
      setMetaTag('meta[name="robots"]', 'noindex, nofollow', { name: 'robots' });
    } else {
      setMetaTag('meta[name="robots"]', 'index, follow', { name: 'robots' });
    }

    // Canonical URL
    setLinkTag('canonical', pageUrl);

    // Open Graph / Facebook
    setMetaTag('meta[property="og:type"]', type, { property: 'og:type' });
    setMetaTag('meta[property="og:url"]', pageUrl, { property: 'og:url' });
    setMetaTag('meta[property="og:title"]', pageTitle, { property: 'og:title' });
    setMetaTag('meta[property="og:description"]', pageDescription, { property: 'og:description' });
    setMetaTag('meta[property="og:image"]', pageImage, { property: 'og:image' });
    setMetaTag('meta[property="og:site_name"]', SITE_NAME, { property: 'og:site_name' });

    // Twitter
    setMetaTag('meta[name="twitter:card"]', 'summary_large_image', { name: 'twitter:card' });
    setMetaTag('meta[name="twitter:url"]', pageUrl, { name: 'twitter:url' });
    setMetaTag('meta[name="twitter:title"]', pageTitle, { name: 'twitter:title' });
    setMetaTag('meta[name="twitter:description"]', pageDescription, { name: 'twitter:description' });
    setMetaTag('meta[name="twitter:image"]', pageImage, { name: 'twitter:image' });

    // Structured Data - Product
    if (product) {
      const productSchema = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: product.name,
        description: product.description || pageDescription,
        image: product.image || pageImage,
        brand: {
          '@type': 'Brand',
          name: SITE_NAME
        },
        ...(product.price && {
          offers: {
            '@type': 'Offer',
            price: product.price,
            priceCurrency: product.currency || 'USD',
            availability: 'https://schema.org/InStock',
            url: pageUrl
          }
        })
      };
      setJsonLd('seo-product-schema', productSchema);
    } else {
      removeJsonLd('seo-product-schema');
    }

    // Structured Data - Breadcrumbs
    if (breadcrumbs && breadcrumbs.length > 0) {
      const breadcrumbSchema = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((crumb, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: crumb.name,
          ...(crumb.url && { item: `${siteUrl}${crumb.url}` })
        }))
      };
      setJsonLd('seo-breadcrumb-schema', breadcrumbSchema);
    } else {
      removeJsonLd('seo-breadcrumb-schema');
    }

    // Structured Data - Organization (for homepage)
    if (type === 'website' && !title) {
      const orgSchema = {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: SITE_NAME,
        url: siteUrl,
        logo: `${siteUrl}/favicon.png`,
        description: DEFAULT_DESCRIPTION,
        sameAs: []
      };
      setJsonLd('seo-org-schema', orgSchema);
    } else {
      removeJsonLd('seo-org-schema');
    }

    // Structured Data - WebSite with SearchAction (for homepage)
    if (isHomepage) {
      const websiteSchema = {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: SITE_NAME,
        url: siteUrl,
        description: DEFAULT_DESCRIPTION,
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${siteUrl}/search?q={search_term_string}`
          },
          'query-input': 'required name=search_term_string'
        }
      };
      setJsonLd('seo-website-schema', websiteSchema);
    } else {
      removeJsonLd('seo-website-schema');
    }

    // Structured Data - ItemList (for category/subcategory pages)
    if (itemList && itemList.length > 0) {
      const itemListSchema = {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: title || 'Collection',
        description: pageDescription,
        numberOfItems: itemList.length,
        itemListElement: itemList.slice(0, 10).map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.name,
          url: item.url?.startsWith('http') ? item.url : `${siteUrl}${item.url}`,
          ...(item.image && { image: item.image })
        }))
      };
      setJsonLd('seo-itemlist-schema', itemListSchema);
    } else {
      removeJsonLd('seo-itemlist-schema');
    }

    // Structured Data - ProductList (for Editor's Picks, featured collections)
    if (productList && productList.length > 0) {
      const productListSchema = {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: title || "Editor's Picks",
        description: pageDescription,
        numberOfItems: productList.length,
        itemListElement: productList.slice(0, 10).map((product, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@type': 'Product',
            name: product.name,
            description: product.description,
            image: product.image,
            url: product.url?.startsWith('http') ? product.url : `${siteUrl}${product.url}`,
            ...(product.category && {
              category: product.category
            }),
            brand: {
              '@type': 'Brand',
              name: SITE_NAME
            }
          }
        }))
      };
      setJsonLd('seo-productlist-schema', productListSchema);
    } else {
      removeJsonLd('seo-productlist-schema');
    }

    // Structured Data - WebPage (for static/article pages)
    if (webPage) {
      const webPageSchema = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: webPage.name || title || 'Page',
        description: webPage.description || pageDescription,
        url: pageUrl,
        isPartOf: {
          '@type': 'WebSite',
          name: SITE_NAME,
          url: siteUrl
        },
        ...(webPage.dateModified && { dateModified: webPage.dateModified }),
        ...(webPage.datePublished && { datePublished: webPage.datePublished }),
        publisher: {
          '@type': 'Organization',
          name: SITE_NAME,
          url: siteUrl
        }
      };
      setJsonLd('seo-webpage-schema', webPageSchema);
    } else {
      removeJsonLd('seo-webpage-schema');
    }

    // Hreflang tags for region targeting
    const setHreflang = (rel, href, hreflang) => {
      const selector = `link[rel="${rel}"][hreflang="${hreflang}"]`;
      let link = document.querySelector(selector);
      if (!link) {
        link = document.createElement('link');
        link.rel = rel;
        link.hreflang = hreflang;
        document.head.appendChild(link);
      }
      link.href = href;
    };

    const removeHreflang = (hreflang) => {
      const link = document.querySelector(`link[rel="alternate"][hreflang="${hreflang}"]`);
      if (link) link.remove();
    };

    // Add hreflang tags for US/UK targeting
    if (region || url) {
      const basePath = url || window.location.pathname;
      setHreflang('alternate', `${siteUrl}${basePath}`, 'en-US');
      setHreflang('alternate', `${siteUrl}${basePath}`, 'en-GB');
      setHreflang('alternate', `${siteUrl}${basePath}`, 'x-default');
    }

    // Cleanup function - reset to defaults on unmount
    return () => {
      // Only clean up structured data, leave meta tags for next page
      removeJsonLd('seo-product-schema');
      removeJsonLd('seo-breadcrumb-schema');
      removeJsonLd('seo-org-schema');
      removeJsonLd('seo-website-schema');
      removeJsonLd('seo-itemlist-schema');
      removeJsonLd('seo-productlist-schema');
      removeJsonLd('seo-webpage-schema');
      removeHreflang('en-US');
      removeHreflang('en-GB');
      removeHreflang('x-default');
    };
  }, [title, description, keywords, image, url, type, product, breadcrumbs, itemList, productList, webPage, isHomepage, region, noIndex]);

  // This component doesn't render anything - it only manipulates document.head
  return null;
};

export default SEO;
